// =============================================================================
// FILE: packages/renderer/src/components/help/SystemInfoDialog.tsx
// PURPOSE: Help → Migration & System Info. Surfaces the consolidated
//          "where does my data live" descriptor returned by IPC.SYSTEM_INFO
//          plus a one-click button that opens the bundled Migration Guide.
// =============================================================================

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Clipboard, FileText, X } from 'lucide-react';
import { call } from '../../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

interface SystemInfo {
  product:       string;
  version:       string;
  buildId:       string;
  buildDate:     string;
  portable:      boolean;
  portableRoot:  string;
  dataDirectory: string;
  databasePath:  string;
  databaseExists: boolean;
  databaseSizeBytes: number;
  logsDirectory: string;
  docsRoot:      string;
  startupLog:    string | null;
  runtime: {
    electron: string;
    chrome:   string;
    node:     string;
    v8:       string;
    platform: string;
    arch:     string;
  };
}

interface Props { open: boolean; onClose: () => void; }

function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n; let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 2)} ${units[u]}`;
}

export function SystemInfoDialog({ open, onClose }: Props): JSX.Element | null {
  const info = useQuery<SystemInfo>({
    queryKey: ['system', 'info'],
    queryFn:  () => call<undefined, SystemInfo>(IPC.SYSTEM_INFO, undefined),
    enabled:  open,
    refetchOnWindowFocus: false,
  });

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const d = info.data;

  const copy = async (label: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(label);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // eslint-disable-next-line no-alert
      window.alert('Clipboard access denied.');
    }
  };

  const openMigrationGuide = async (): Promise<void> => {
    const result = await call<{ name: string }, { ok: boolean; error?: string }>(
      IPC.HELP_OPEN_DOC,
      { name: 'MIGRATION_GUIDE.pdf' },
    );
    if (!result.ok) {
      // eslint-disable-next-line no-alert
      window.alert(`Could not open Migration Guide\n\n${result.error ?? 'unknown error'}`);
    }
  };

  return (
    <div
      data-testid="system-info-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-sm shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between p-6 pb-4 border-b border-zinc-900">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Help</div>
            <h2 className="text-lg font-semibold text-zinc-100">Migration & System Info</h2>
            <p className="text-[12px] text-zinc-500 mt-1 max-w-lg">
              Everything you need to back up, move, or rebuild this installation.
              Paths are absolute and current. Click any value to copy.
            </p>
          </div>
          <button
            type="button"
            data-testid="system-info-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
          {info.isLoading && (
            <div data-testid="system-info-loading" className="text-[12px] text-zinc-500">
              Loading system information…
            </div>
          )}
          {info.error && (
            <div data-testid="system-info-error" className="text-[12px] text-rose-400">
              Failed to load system information: {(info.error as Error).message}
            </div>
          )}
          {d && (
            <>
              <Section title="Product">
                <Row label="Product"  value={d.product}   onCopy={copy} copiedKey={copiedKey} />
                <Row label="Version"  value={d.version}   mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Build ID" value={d.buildId}   mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Build date" value={d.buildDate} mono onCopy={copy} copiedKey={copiedKey} />
              </Section>

              <Section title="Paths" testid="system-info-paths">
                <Row label="Portable root"  value={d.portableRoot}  mono onCopy={copy} copiedKey={copiedKey} testid="sys-portable-root" />
                <Row label="Data directory" value={d.dataDirectory} mono onCopy={copy} copiedKey={copiedKey} testid="sys-data-dir" />
                <Row label="Database path"  value={d.databasePath}  mono onCopy={copy} copiedKey={copiedKey} testid="sys-db-path" />
                <Row label="Database size"  value={d.databaseExists ? bytes(d.databaseSizeBytes) : 'missing'} mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Logs directory" value={d.logsDirectory} mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Docs root"      value={d.docsRoot}      mono onCopy={copy} copiedKey={copiedKey} />
                {d.startupLog && (
                  <Row label="Startup log" value={d.startupLog} mono onCopy={copy} copiedKey={copiedKey} />
                )}
                <Row label="Portable"       value={d.portable ? 'true' : 'false'} mono onCopy={copy} copiedKey={copiedKey} />
              </Section>

              <Section title="Runtime">
                <Row label="Electron" value={d.runtime.electron} mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Chromium" value={d.runtime.chrome}   mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Node"     value={d.runtime.node}     mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="V8"       value={d.runtime.v8}       mono onCopy={copy} copiedKey={copiedKey} />
                <Row label="Platform" value={`${d.runtime.platform} · ${d.runtime.arch}`} mono onCopy={copy} copiedKey={copiedKey} />
              </Section>
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 p-4 border-t border-zinc-900 bg-zinc-950">
          <button
            type="button"
            data-testid="system-info-open-migration-guide"
            onClick={openMigrationGuide}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] rounded-sm border border-amber-700/60 bg-amber-950/40 text-amber-200 hover:bg-amber-900/40 transition"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Open Migration Guide
          </button>
          <div className="text-[10px] text-zinc-600 inline-flex items-center gap-2">
            <FileText className="h-3 w-3" />
            <span>All paths are absolute and current.</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface SectionProps { title: string; children: React.ReactNode; testid?: string }
function Section({ title, children, testid }: SectionProps): JSX.Element {
  return (
    <section data-testid={testid} className="border border-zinc-900 rounded-sm">
      <div className="px-4 py-2 border-b border-zinc-900 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        {title}
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </section>
  );
}

interface RowProps {
  label: string;
  value: string;
  mono?: boolean;
  testid?: string;
  copiedKey: string | null;
  onCopy(label: string, value: string): void;
}
function Row({ label, value, mono, testid, copiedKey, onCopy }: RowProps): JSX.Element {
  const copied = copiedKey === label;
  return (
    <div className="grid grid-cols-[140px_1fr_auto] items-center gap-3" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</span>
      <span className={['text-zinc-200 break-all', mono ? 'font-mono text-[11.5px]' : 'text-[13px]'].join(' ')}>
        {value || '—'}
      </span>
      <button
        type="button"
        onClick={() => onCopy(label, value)}
        aria-label={`Copy ${label}`}
        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.18em] rounded-sm border border-zinc-800 text-zinc-500 hover:text-zinc-200 hover:border-zinc-700 transition"
      >
        <Clipboard className="h-3 w-3" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
